/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * DAS Topology View
 * Main container for the graphical topology view
 * Combines tree sidebar, canvas, and toolbar
 */

import React, { useState, useMemo, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { IInventoryGraphNode, IInventoryGraphEdge, IRemoteEquipment } from "../../store/api/devices/types";
import { TopologyNode, Transform } from "./types";
import { DasTreeSidebar } from "./DasTreeSidebar";
import { DasTopologyCanvas } from "./DasTopologyCanvas";
import { TopologyToolbar } from "./TopologyToolbar";
import { platformRegistry } from "./platforms";
import { resolveDeviceStatusLabel } from "../../utils/deviceStatus";

interface DasTopologyViewProps {
  nodes: IInventoryGraphNode[];
  edges: IInventoryGraphEdge[];
  rootId: string;
  onDeviceClick: (device: IRemoteEquipment) => void;
  listData: IRemoteEquipment[]; // For mapping node IDs to device data
  platform?: string; // "ma3000" or "ma6200g1" or "ma6200g2"
  filterEquipmentType?: string;
  filterStatus?: string;
  filterBand?: string;
  filterSearchKeyword?: string;
}

// Helper function to get all descendant IDs
const getAllDescendantIds = (node: TopologyNode | null): string[] => {
  if (!node) return [];
  const ids: string[] = [];
  const traverse = (n: TopologyNode) => {
    n.children.forEach((child) => {
      ids.push(child.id);
      traverse(child);
    });
  };
  traverse(node);
  return ids;
};

// Helper function to get only first-level children IDs
const getFirstLevelChildrenIds = (node: TopologyNode | null): string[] => {
  if (!node) return [];
  return node.children.map(child => child.id);
};

// Helper function to count total nodes in hierarchy
const countTotalNodes = (node: TopologyNode | null): number => {
  if (!node) return 0;
  let count = 1; // Count the current node
  const traverse = (n: TopologyNode) => {
    n.children.forEach((child) => {
      count++;
      traverse(child);
    });
  };
  traverse(node);
  return count;
};

// Helper function to build a map of node ID to parent ID
const buildParentMap = (node: TopologyNode | null): Map<string, string> => {
  const parentMap = new Map<string, string>();
  if (!node) return parentMap;
  
  const traverse = (n: TopologyNode, parentId: string | null) => {
    if (parentId) {
      parentMap.set(n.id, parentId);
    }
    n.children.forEach((child) => {
      traverse(child, n.id);
    });
  };
  
  traverse(node, null);
  return parentMap;
};

// Helper function to get all ancestor IDs for a node
const getAncestorIds = (nodeId: string, parentMap: Map<string, string>): string[] => {
  const ancestors: string[] = [];
  let currentId: string | undefined = nodeId;
  
  while (currentId) {
    const parentId = parentMap.get(currentId);
    if (parentId) {
      ancestors.push(parentId);
      currentId = parentId;
    } else {
      break;
    }
  }
  
  return ancestors;
};

export const DasTopologyView: React.FC<DasTopologyViewProps> = ({
  nodes,
  edges,
  rootId,
  onDeviceClick,
  listData,
  platform = "ma3000", // Default to ma3000 for backward compatibility
  filterEquipmentType,
  filterStatus,
  filterBand,
  filterSearchKeyword,
}) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [showLabels, setShowLabels] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [transform, setTransform] = useState<Transform>({ x: 10, y: -400, scale: 2.55 });
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [typeFilter, setTypeFilter] = useState<string>("All");
  const hasInitializedCollapsedState = useRef(false);

  // Get platform configuration
  const platformConfig = useMemo(() => {
    return platformRegistry.getPlatform(platform);
  }, [platform]);

  // Get root device LED color using platform-specific logic
  const rootLedColor = useMemo(() => {
    if (platformConfig.getRootLedColor) {
      return platformConfig.getRootLedColor(listData);
    }
    return "#9e9e9e"; // Default gray
  }, [listData, platformConfig]);

  // Build hierarchy tree using platform-specific builder
  const hierarchyRoot = useMemo(() => {
    console.log(`[DasTopologyView] Building hierarchy for platform: ${platformConfig.displayName} (${platformConfig.platformId})`);
    
    // Provide equipment data to hierarchy builder
    platformConfig.hierarchyBuilder.setEquipmentData(listData);
    
    // Log equipment types in listData
    const equipmentTypes = new Set<string>();
    listData.forEach(d => {
      if (d.equipmentType) equipmentTypes.add(d.equipmentType);
    });
    console.log('[DasTopologyView] Equipment types in listData:', Array.from(equipmentTypes).sort());
    console.log('[DasTopologyView] Total devices in listData:', listData.length);
    
    // Log sample devices
    if (listData.length > 0) {
      console.log('[DasTopologyView] Sample device:', listData[0]);
    }
    
    const root = platformConfig.hierarchyBuilder.buildHierarchy(nodes, edges, rootId);
    
    // Log total node count and collapse strategy
    if (root) {
      const totalNodes = countTotalNodes(root);
      console.log(`[DasTopologyView] Total nodes in hierarchy: ${totalNodes}`);
      console.log(`[DasTopologyView] Initial state: ${totalNodes > 20 ? 'All collapsed' : 'First level expanded'}`);
    }
    
    return root;
  }, [nodes, edges, rootId, listData, platformConfig]);

  // Extract available filter options from hierarchy
  const availableFilters = useMemo(() => {
    const statuses = new Set<string>();
    const types = new Set<string>();
    
    const traverse = (node: TopologyNode) => {
      if (node.status) statuses.add(node.status);
      if (node.equipmentType) types.add(node.equipmentType);
      node.children.forEach(traverse);
    };
    
    if (hierarchyRoot) traverse(hierarchyRoot);
    
    console.log('[DasTopologyView] Available equipment types from hierarchy:', Array.from(types).sort());
    console.log('[DasTopologyView] Available statuses from hierarchy:', Array.from(statuses).sort());
    
    return {
      availableStatuses: ["All", ...Array.from(statuses).sort()],
      availableTypes: ["All", ...Array.from(types).sort()]
    };
  }, [hierarchyRoot]);

  // Helper function to check if a node matches the inventory filters
  const nodeMatchesInventoryFilters = useCallback((node: TopologyNode): boolean => {
    // Try to find the corresponding device in listData
    const device = listData.find(
      (d) =>
        d.objectKey === node.id ||
        d.hostname === node.label ||
        d.ipAddress === node.ipAddress ||
        d.serialNumber === node.serialNumber
    );
    
    // Apply equipment type filter
    // Match against topology node's equipmentType directly, not just from listData
    if (filterEquipmentType) {
      const nodeEquipmentType = device?.equipmentType || node.equipmentType;
      const matches = nodeEquipmentType === filterEquipmentType;
      
      if (matches) {
        console.log('[DasTopologyView] Node matches filter:', {
          nodeId: node.id,
          nodeLabel: node.label,
          nodeEquipmentType: node.equipmentType,
          deviceEquipmentType: device?.equipmentType,
          filterEquipmentType
        });
      }
      
      if (!matches) {
        return false;
      }
    }
    
    // For other filters, we need the device data
    // If no device found and we have non-type filters, this node doesn't match
    if (!device && (filterStatus || filterBand || (filterSearchKeyword && filterSearchKeyword.length >= 3))) {
      return false;
    }
    
    // Apply status filter
    if (device && filterStatus) {
      const statusLabel = resolveDeviceStatusLabel(device.status);
      if (statusLabel !== filterStatus) {
        return false;
      }
    }
    
    // Apply band filter
    if (device && filterBand && device.band !== filterBand) {
      return false;
    }
    
    // Apply search filter (search in hostname and serialNumber)
    if (device && filterSearchKeyword && filterSearchKeyword.length >= 3) {
      const keyword = filterSearchKeyword.toLowerCase();
      const hostname = (device.hostname || "").toLowerCase();
      const serialNumber = (device.serialNumber || "").toLowerCase();
      if (!hostname.includes(keyword) && !serialNumber.includes(keyword)) {
        return false;
      }
    }
    
    return true;
  }, [listData, filterEquipmentType, filterStatus, filterBand, filterSearchKeyword]);

  // Find all matching nodes based on inventory filters
  const findMatchingNodes = useCallback((root: TopologyNode | null): string[] => {
    if (!root) return [];
    const matchingIds: string[] = [];
    
    const traverse = (node: TopologyNode) => {
      if (nodeMatchesInventoryFilters(node)) {
        matchingIds.push(node.id);
      }
      node.children.forEach(traverse);
    };
    
    traverse(root);
    return matchingIds;
  }, [nodeMatchesInventoryFilters]);

  // Compute which nodes should be visible in canvas when inventory filters are active
  // This includes matching nodes AND their ancestors to show the full path
  const visibleNodeIdsForInventoryFilter = useMemo(() => {
    // Check if any inventory filter is active
    const hasActiveFilter = 
      filterEquipmentType || 
      filterStatus || 
      filterBand || 
      (filterSearchKeyword && filterSearchKeyword.length >= 3);
    
    console.log('[DasTopologyView] Inventory filters:', {
      filterEquipmentType,
      filterStatus,
      filterBand,
      filterSearchKeyword,
      hasActiveFilter
    });
    
    if (!hasActiveFilter || !hierarchyRoot) {
      console.log('[DasTopologyView] No inventory filter active, showing all nodes');
      return null; // No filter active, show all nodes
    }
    
    // Find all matching nodes
    const matchingNodeIds = findMatchingNodes(hierarchyRoot);
    
    console.log('[DasTopologyView] Found', matchingNodeIds.length, 'matching nodes:', matchingNodeIds);
    
    if (matchingNodeIds.length === 0) {
      console.log('[DasTopologyView] No matches, showing empty set');
      return new Set<string>(); // No matches, show nothing
    }
    
    // Build parent map to trace ancestry
    const parentMap = buildParentMap(hierarchyRoot);
    
    console.log('[DasTopologyView] Parent map size:', parentMap.size);
    console.log('[DasTopologyView] Parent map sample (first 5):', 
      Array.from(parentMap.entries()).slice(0, 5).map(([child, parent]) => ({ child, parent }))
    );
    
    // Collect matching nodes and all their ancestors
    const visibleIds = new Set<string>(matchingNodeIds);
    matchingNodeIds.forEach((nodeId) => {
      const ancestors = getAncestorIds(nodeId, parentMap);
      console.log(`[DasTopologyView] Ancestors for ${nodeId}:`, ancestors);
      ancestors.forEach((ancestorId) => visibleIds.add(ancestorId));
    });
    
    // Always include root
    console.log('[DasTopologyView] Root ID:', hierarchyRoot.id);
    visibleIds.add(hierarchyRoot.id);
    
    console.log('[DasTopologyView] Visible node IDs (including ancestors):', Array.from(visibleIds));
    
    // Log visible nodes with their details for debugging
    const visibleNodesDetails: any[] = [];
    const traverseForDetails = (node: TopologyNode) => {
      if (visibleIds.has(node.id)) {
        visibleNodesDetails.push({
          id: node.id,
          label: node.label,
          type: node.equipmentType
        });
      }
      node.children.forEach(traverseForDetails);
    };
    traverseForDetails(hierarchyRoot);
    console.log('[DasTopologyView] Visible nodes details:', visibleNodesDetails);
    
    return visibleIds;
  }, [hierarchyRoot, filterEquipmentType, filterStatus, filterBand, filterSearchKeyword, findMatchingNodes]);

  // Initialize collapsed state - collapse all except first level
  // If there are more than 20 nodes, start with everything collapsed
  const [collapsedNodeIds, setCollapsedNodeIds] = useState<Set<string>>(() => {
    if (!hierarchyRoot) return new Set();
    const totalNodes = countTotalNodes(hierarchyRoot);
    const allDescendantIds = getAllDescendantIds(hierarchyRoot);
    
    // If more than 20 nodes, collapse everything (including first level)
    if (totalNodes > 20) {
      return new Set(allDescendantIds);
    }
    
    // Otherwise, collapse everything except root's direct children
    const firstLevelIds = getFirstLevelChildrenIds(hierarchyRoot);
    return new Set(allDescendantIds.filter(id => !firstLevelIds.includes(id)));
  });

  // Preserve collapsed state when hierarchy object is rebuilt (for example, opening/closing drawer)
  // Only initialize defaults on first load. After that, keep user state for existing IDs.
  React.useEffect(() => {
    if (hierarchyRoot) {
      const allDescendantIds = getAllDescendantIds(hierarchyRoot);

      setCollapsedNodeIds((prev) => {
        // Initial mount: keep existing behavior
        if (!hasInitializedCollapsedState.current) {
          hasInitializedCollapsedState.current = true;
          const totalNodes = countTotalNodes(hierarchyRoot);

          if (totalNodes > 20) {
            return new Set(allDescendantIds);
          }

          const firstLevelIds = getFirstLevelChildrenIds(hierarchyRoot);
          return new Set(allDescendantIds.filter(id => !firstLevelIds.includes(id)));
        }

        // Subsequent updates: preserve previous collapse state for IDs that still exist
        const currentIdSet = new Set(allDescendantIds);
        const preserved = new Set(Array.from(prev).filter((id) => currentIdSet.has(id)));

        return preserved;
      });
    }
  }, [hierarchyRoot]);

  // Auto-expand ancestry when inventory filters are applied
  React.useEffect(() => {
    // Check if any inventory filter is active
    const hasActiveFilter = 
      filterEquipmentType || 
      filterStatus || 
      filterBand || 
      (filterSearchKeyword && filterSearchKeyword.length >= 3);
    
    if (!hasActiveFilter || !hierarchyRoot) {
      return;
    }
    
    // Find all matching nodes
    const matchingNodeIds = findMatchingNodes(hierarchyRoot);
    
    if (matchingNodeIds.length === 0) {
      return;
    }
    
    // Build parent map to trace ancestry
    const parentMap = buildParentMap(hierarchyRoot);
    
    // Collect all ancestor IDs for all matching nodes
    const ancestorIdsToExpand = new Set<string>();
    matchingNodeIds.forEach((nodeId) => {
      const ancestors = getAncestorIds(nodeId, parentMap);
      ancestors.forEach((ancestorId) => ancestorIdsToExpand.add(ancestorId));
    });
    
    // Update collapsed state to expand ancestors (remove from collapsed set)
    setCollapsedNodeIds((prev) => {
      const next = new Set(prev);
      ancestorIdsToExpand.forEach((ancestorId) => {
        next.delete(ancestorId);
      });
      return next;
    });
  }, [hierarchyRoot, filterEquipmentType, filterStatus, filterBand, filterSearchKeyword, findMatchingNodes]);

  // Handle node click - open device drawer using platform-specific logic
  const handleNodeClick = useCallback(
    (nodeId: string) => {
      setSelectedNodeId(nodeId);
      
      console.log('[DasTopologyView] Node clicked:', nodeId);
      
      // Find the graph node
      const node = nodes.find((n) => n.id === nodeId);
      console.log('[DasTopologyView] Found graph node:', node?.id, node?.equipmentType, node?.label);
      
      // Use platform-specific device finder
      const device = platformConfig.findDeviceForNode(nodeId, node, listData, rootId);
      
      if (device) {
        console.log(`[DasTopologyView] Found device for ${platformConfig.displayName}:`, device.objectKey, device.equipmentType);
        onDeviceClick(device);
      } else {
        console.warn(`[DasTopologyView] No device found for node in ${platformConfig.displayName}:`, nodeId, node);
      }
    },
    [nodes, listData, onDeviceClick, rootId, platformConfig]
  );

  // Handle node collapse/expand
  const handleNodeToggle = useCallback((nodeId: string) => {
    setCollapsedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Expand all nodes
  const handleExpandAll = useCallback(() => {
    setCollapsedNodeIds(new Set());
  }, []);

  // Collapse all except first level
  const handleCollapseAll = useCallback(() => {
    if (hierarchyRoot) {
      // If currently all expanded, collapse to first level only
      // If already partially collapsed, collapse everything including first level
      if (collapsedNodeIds.size === 0) {
        // Coming from expanded all - collapse to first level only
        const firstLevelIds = getFirstLevelChildrenIds(hierarchyRoot);
        const allDescendantIds = getAllDescendantIds(hierarchyRoot);
        setCollapsedNodeIds(new Set(allDescendantIds.filter(id => !firstLevelIds.includes(id))));
      } else {
        // Already partially collapsed - collapse everything including first level
        const allDescendantIds = getAllDescendantIds(hierarchyRoot);
        setCollapsedNodeIds(new Set(allDescendantIds));
      }
    }
  }, [hierarchyRoot, collapsedNodeIds]);

  // Reset view
  const handleReset = useCallback(() => {
    setTransform({ x: 10, y: -400, scale: 2.55 });
    setStatusFilter("All");
    setTypeFilter("All");
    if (hierarchyRoot) {
      const firstLevelIds = getFirstLevelChildrenIds(hierarchyRoot);
      const allDescendantIds = getAllDescendantIds(hierarchyRoot);
      setCollapsedNodeIds(new Set(allDescendantIds.filter(id => !firstLevelIds.includes(id))));
    }
  }, [hierarchyRoot]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 250px)", // Adjust based on your layout
        minHeight: 600,
        backgroundColor: "#020617", // slate-950 (mockup)
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      {/* Left sidebar - tree view */}
      <DasTreeSidebar
        hcmLedColor={rootLedColor}
        root={hierarchyRoot}
        collapsedNodeIds={collapsedNodeIds}
        selectedNodeId={selectedNodeId}
        isCollapsed={sidebarCollapsed}
        statusFilter={statusFilter}
        typeFilter={typeFilter}
        visibleNodeIdsForInventoryFilter={visibleNodeIdsForInventoryFilter}
        onNodeClick={handleNodeClick}
        onNodeToggle={handleNodeToggle}
        onSidebarToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Right side - canvas and toolbar */}
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        {/* Toolbar */}
        <TopologyToolbar
          transform={transform}
          showLabels={showLabels}
          statusFilter={statusFilter}
          typeFilter={typeFilter}
          collapsedCount={collapsedNodeIds.size}
          availableStatuses={availableFilters.availableStatuses}
          availableTypes={availableFilters.availableTypes}
          onTransformChange={setTransform}
          onShowLabelsChange={setShowLabels}
          onStatusFilterChange={setStatusFilter}
          onTypeFilterChange={setTypeFilter}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
          onReset={handleReset}
        />

        {/* Canvas */}
        <Box sx={{ flex: 1 }}>
          <DasTopologyCanvas
            root={hierarchyRoot}
            collapsedNodeIds={collapsedNodeIds}
            selectedNodeId={selectedNodeId}
            showLabels={showLabels}
            statusFilter={statusFilter}
            typeFilter={typeFilter}
            hcmLedColor={rootLedColor}
            visibleNodeIdsForInventoryFilter={visibleNodeIdsForInventoryFilter}
            onNodeClick={handleNodeClick}
            onNodeCollapse={handleNodeToggle}
            transform={transform}
            onTransformChange={setTransform}
          />
        </Box>
      </Box>
    </Box>
  );
};
